import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { firebaseConfig, auth, googleProvider } from '../firebase-config';

// Debug: Log the Firebase config
console.log('Firebase Config:', firebaseConfig);

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [allowSignup, setAllowSignup] = useState(false);

  // Check for redirect result on app load
  useEffect(() => {
    const checkRedirectResult = async () => {
      console.log('🔍 Checking for redirect result...');
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('🚨 REDIRECT RESULT FOUND!', result.user.email);
          await handleSignInCredential(result, 'Google');
        } else {
          console.log('🔍 No redirect result found');
        }
      } catch (error) {
        console.error('Error checking redirect result:', error);
      }
    };
    
    checkRedirectResult();
  }, []);

  // Handle sign-in credential (shared between popup and redirect)
  const handleSignInCredential = async (result, providerName) => {
    try {
      const user = result.user;
      console.log('Google Sign-In successful:', user.email);
      
      // Call our new backend function to create/check Google user profile
      const response = await fetch('https://us-central1-twerceapp.cloudfunctions.net/createGoogleUserProfile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        })
      });

      const result = await response.json();

      if (!result.success) {
        console.error('Failed to create Google user profile:', result.error);
        return { success: false, error: result.error };
      }

      console.log('Google user profile handled successfully');
      return { success: true, user };
    } catch (error) {
      console.error('Error handling sign-in credential:', error);
      return { success: false, error: error.message };
    }
  };

  // Sign up function
  const signUp = async (email, password, userData) => {
    console.log('🚨 SIGNUP FUNCTION CALLED!', {
      email,
      hasPassword: !!password,
      userData: userData ? Object.keys(userData) : null,
      timestamp: new Date().toISOString(),
      stack: new Error().stack,
      allowSignup
    });
    
    if (!allowSignup) {
      console.log('🚨 SIGNUP BLOCKED - allowSignup is false');
      return { success: false, error: 'Signup not allowed at this time' };
    }
    
    try {
      // Call our new backend signup function
      const response = await fetch('https://us-central1-twerceapp.cloudfunctions.net/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          userData
        })
      });

      const result = await response.json();

      if (!result.success) {
        return { success: false, error: result.error };
      }

      // Sign in the user with Firebase Auth on the client side
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update profile with display name
      await updateProfile(user, {
        displayName: userData.fullName
      });

      return { success: true, user };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  // Sign in function
  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Google Sign In function
  const signInWithGoogle = async () => {
    try {
      console.log('Starting Google Sign-In with popup...');
      const result = await signInWithPopup(auth, googleProvider);
      return await handleSignInCredential(result, 'Google');
    } catch (error) {
      console.error('Popup Google Sign-In Error:', error);
      
      // If popup fails, try redirect
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        try {
          console.log('Popup failed, trying redirect...');
          await signInWithRedirect(auth, googleProvider);
          return { success: true, message: 'Redirecting to Google Sign-In...' };
        } catch (redirectError) {
          console.error('Redirect Google Sign-In Error:', redirectError);
          return { success: false, error: 'Google sign-in failed. Please try again.' };
        }
      }
      
      // Provide user-friendly error messages
      let errorMessage = 'Google sign-in failed. Please try again.';
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled. Please try again.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Sign-in was cancelled. Please try again.';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different sign-in method.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Google sign-in is not enabled. Please contact support.';
          break;
        case 'auth/internal-error':
          errorMessage = 'Google sign-in configuration error. Please check your Firebase setup.';
          break;
        default:
          errorMessage = error.message || 'Google sign-in failed. Please try again.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Sign out function
  const signOutUser = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Create RFQ function
  const createRFQ = async (rfqData) => {
    try {
      // Only get ID token if user is authenticated
      let idToken = null;
      if (user) {
        idToken = await user.getIdToken();
      }
      
      // Call the backend API
      const functionsUrl = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-twerceapp.cloudfunctions.net';
      console.log('Using functions URL:', functionsUrl);
      
      const response = await fetch(`${functionsUrl}/createRFQ`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify(rfqData)
      });

      const result = await response.json();
      
      console.log('Backend response status:', response.status);
      console.log('Backend response:', result);
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create RFQ');
      }

      return result;
    } catch (error) {
      console.error('Error creating RFQ:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return { success: false, error: 'Network error. Please check your internet connection and try again.' };
      }
      return { success: false, error: error.message };
    }
  };

  // Get user's RFQs
  const getUserRFQs = async () => {
    try {
      const q = query(
        collection(db, 'posts'),
        where('author', '==', user.uid),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const rfqs = [];
      querySnapshot.forEach((doc) => {
        rfqs.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, rfqs };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Delete RFQ function
  const deleteRFQ = async (rfqId) => {
    try {
      // First check if the RFQ belongs to the current user
      const rfqDoc = await getDoc(doc(db, 'posts', rfqId));
      if (!rfqDoc.exists()) {
        return { success: false, error: 'RFQ not found' };
      }
      
      const rfqData = rfqDoc.data();
      if (rfqData.author !== user.uid) {
        return { success: false, error: 'You can only delete your own RFQs' };
      }

      // Delete the RFQ
      await deleteDoc(doc(db, 'posts', rfqId));
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Get market feed (all RFQs)
  const getMarketFeed = async () => {
    try {
      const q = query(
        collection(db, 'posts'),
        where('approved', '==', true),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const querySnapshot = await getDocs(q);
      const rfqs = [];
      querySnapshot.forEach((doc) => {
        rfqs.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, rfqs };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Get responses to user's RFQs
  const getResponsesToUserRFQs = async () => {
    try {
      // First get all posts by the current user
      const userPostsQuery = query(
        collection(db, 'posts'),
        where('author', '==', user.uid)
      );
      const userPostsSnapshot = await getDocs(userPostsQuery);
      const userPosts = {};
      userPostsSnapshot.docs.forEach(doc => {
        userPosts[doc.id] = { id: doc.id, ...doc.data() };
      });

      if (Object.keys(userPosts).length === 0) {
        return { success: true, responses: [] };
      }

      // Then get all replies to these posts
      const responses = [];
      for (const postId of Object.keys(userPosts)) {
        const repliesQuery = query(
          collection(db, 'replies'),
          where('postId', '==', postId),
          orderBy('timestamp', 'desc')
        );
        const repliesSnapshot = await getDocs(repliesQuery);
        repliesSnapshot.forEach((doc) => {
          const originalPost = userPosts[postId];
          responses.push({ 
            id: doc.id, 
            ...doc.data(),
            originalPostId: postId,
            originalPostTitle: originalPost.textContent || originalPost.title || 'Your RFQ',
            originalPostBudget: originalPost.budget,
            originalPostCategory: originalPost.categories?.[0] || 'General'
          });
        });
      }

      // Sort responses by timestamp (newest first)
      responses.sort((a, b) => b.timestamp - a.timestamp);

      return { success: true, responses };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Create or get existing conversation between two users
  const getOrCreateConversation = async (user1, user2) => {
    try {
      // Check if conversation already exists
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', user1.uid)
      );
      
      const querySnapshot = await getDocs(q);
      let existingConversation = null;
      
      querySnapshot.forEach((doc) => {
        const conversation = doc.data();
        if (conversation.participants.some(p => p.uid === user2.uid)) {
          existingConversation = { id: doc.id, ...conversation };
        }
      });

      if (existingConversation) {
        return existingConversation;
      }

      // Create new conversation
      const newConversation = {
        participants: [
          {
            uid: user1.uid,
            name: user1.fullName || user1.displayName || 'User',
            photoUrl: user1.photoUrl || null
          },
          {
            uid: user2.uid,
            name: user2.fullName || user2.displayName || 'User',
            photoUrl: user2.photoUrl || null
          }
        ],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: null,
        lastMessageAuthor: null,
        lastMessageAuthorPhotoUrl: null,
        unreadCount: 0
      };

      const conversationRef = await addDoc(conversationsRef, newConversation);
      return { id: conversationRef.id, ...newConversation };
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  };

  // Get user conversations
  const getUserConversations = async () => {
    try {
      if (!user) return { success: true, conversations: [] };

      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', user.uid),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const conversations = [];
      
      querySnapshot.forEach((doc) => {
        conversations.push({ id: doc.id, ...doc.data() });
      });

      return { success: true, conversations };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Send message to conversation
  const sendMessage = async (conversationId, messageText, receiverId) => {
    try {
      const messageData = {
        conversationId,
        text: messageText,
        sender: user.uid,
        receiver: receiverId,
        createdAt: serverTimestamp(),
        user: {
          _id: user.uid,
          name: userProfile?.fullName || user?.displayName || 'User',
          avatar: userProfile?.photoUrl || null,
        },
        sent: true,
        received: true
      };

      await addDoc(collection(db, 'messages'), messageData);

      // Update conversation with last message
      await setDoc(doc(db, 'conversations', conversationId), {
        lastMessage: messageText,
        lastMessageAuthor: userProfile?.fullName || user?.displayName || 'User',
        lastMessageAuthorPhotoUrl: userProfile?.photoUrl || null,
        updatedAt: serverTimestamp()
      }, { merge: true });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Update user profile
  const updateUserProfile = async (profileData) => {
    try {
      const response = await fetch('https://us-central1-twerceapp.cloudfunctions.net/updateUserProfile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.uid,
          userData: profileData
        })
      });

      const result = await response.json();

      if (result.success) {
        setUserProfile(prev => ({ ...prev, ...profileData }));
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Load user profile from backend API
  const loadUserProfile = async (uid) => {
    try {
      const response = await fetch(`https://us-central1-twerceapp.cloudfunctions.net/getUserProfile?uid=${uid}`);
      const result = await response.json();
      
      if (result.success) {
        setUserProfile(result.user);
      } else {
        console.error('Error loading user profile:', result.error);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  // Get appropriate dashboard path based on user role
  const getDashboardPath = () => {
    console.log('🔍 getDashboardPath called with userProfile:', userProfile);
    
    if (!userProfile) {
      console.log('🔍 No userProfile, returning /supplier-dashboard as default');
      return '/supplier-dashboard';
    }
    
    // Check for admin privileges
    if (userProfile.isAdmin === true || userProfile.role === 'admin') {
      console.log('🔍 User is admin, returning /dashboard');
      return '/dashboard'; // Admin dashboard
    }
    
    // Check user type
    if (userProfile.userType === 'supplier') {
      console.log('🔍 User is supplier, returning /supplier-dashboard');
      return '/supplier-dashboard';
    } else if (userProfile.userType === 'buyer') {
      console.log('🔍 User is buyer, returning /buyer-dashboard');
      return '/buyer-dashboard';
    }
    
    // Default fallback - since we're supplier-focused, default to supplier dashboard
    console.log('🔍 No specific role found, returning /supplier-dashboard as default');
    return '/supplier-dashboard';
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut: signOutUser,
    forgotPassword,
    createRFQ,
    getUserRFQs,
    deleteRFQ,
    getMarketFeed,
    getResponsesToUserRFQs,
    getOrCreateConversation,
    getUserConversations,
    sendMessage,
    updateUserProfile,
    signInWithGoogle,
    signInWithGoogleRedirect: () => signInWithRedirect(auth, googleProvider),
    getDashboardPath
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 